import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  FormControlLabel, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import BeeDeliveryHomologationPanel from './BeeDeliveryHomologationPanel';
import CourierIntentsPanel from './CourierIntentsPanel';
import { apiFetch } from '../utils/apiFetch';
import { isStoredSessionTokenCurrent } from '../utils/auth';
import { isBeeLocalPreviewHost } from '../utils/beeSandboxPreview';

const emptySettings = {
  enabled: false,
  environment: 'production',
  merchantId: '',
  clientId: '',
  clientSecret: '',
  postKey: '',
  appId: '',
};

const toSettings = (data) => ({
  enabled: data?.enabled === true,
  environment: ['production', 'integrationtest'].includes(data?.environment)
    ? data.environment : 'production',
  merchantId: String(data?.merchantId || ''),
  clientId: '',
  clientSecret: '',
  postKey: '',
  appId: String(data?.appId || ''),
});

export { isBeeLocalPreviewHost } from '../utils/beeSandboxPreview';

const BeeDeliverySettingsPanel = ({ apiUrl, sessionToken, canManageHomologation = false }) => {
  const localPreview = typeof window !== 'undefined' && isBeeLocalPreviewHost(window.location.hostname);
  const mountedRef = useRef(false);
  const loadVersionRef = useRef(0);
  const saveVersionRef = useRef(0);
  const saveControllerRef = useRef(null);
  const currentSessionRef = useRef(sessionToken);
  currentSessionRef.current = sessionToken;
  const [settings, setSettings] = useState(emptySettings);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [configRevision,setConfigRevision] = useState(0);

  useEffect(() => {
    mountedRef.current = true;
    const loadVersion = ++loadVersionRef.current;
    const controller = new AbortController();
    setLoading(true);
    setSummary(null);
    setSettings(emptySettings);
    setSaving(false);
    setError('');
    setSaved(false);
    const load = async () => {
      try {
        const response = await apiFetch(`${apiUrl}/api/courier/integrations/bee`, { signal: controller.signal });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Não foi possível carregar a integração Bee Delivery.');
        if (!mountedRef.current || loadVersion !== loadVersionRef.current || !isStoredSessionTokenCurrent(sessionToken)) return;
        setSummary(data);
        setSettings(toSettings(data));
        setError('');
      } catch (loadError) {
        if (loadError.name === 'AbortError' || !mountedRef.current || loadVersion !== loadVersionRef.current) return;
        setError(loadError.message || 'Não foi possível carregar a integração Bee Delivery.');
      } finally {
        if (mountedRef.current && loadVersion === loadVersionRef.current) setLoading(false);
      }
    };
    load();
    return () => {
      mountedRef.current = false;
      loadVersionRef.current += 1;
      saveVersionRef.current += 1;
      controller.abort();
      saveControllerRef.current?.abort();
      saveControllerRef.current = null;
    };
  }, [apiUrl, sessionToken, configRevision]);

  const identityDirty = settings.environment!==summary?.environment || settings.merchantId.trim()!==(summary?.merchantId||'') || settings.appId!==(summary?.appId||'') || Boolean(settings.clientId.trim()||settings.clientSecret.trim()||settings.postKey.trim());
  const update = (field, value) => {
    setSettings((previous) => ({ ...previous, [field]: value, ...(previous.environment==='production' && ['merchantId','clientId','clientSecret','postKey','appId'].includes(field)?{enabled:false}:{}) }));
    setSaved(false);
    setError('');
  };

  const save = async () => {
    if (!isStoredSessionTokenCurrent(sessionToken)) return;
    if (!settings.merchantId.trim()) {
      setError('Informe o identificador da loja Bee Delivery.');
      return;
    }
    setSaving(true);
    setSaved(false);
    setError('');
    const saveVersion = ++saveVersionRef.current;
    saveControllerRef.current?.abort();
    const controller = new AbortController();
    saveControllerRef.current = controller;
    try {
      const disablingExistingBinding = summary?.configured && settings.enabled === false &&
        settings.environment === summary.environment && settings.merchantId.trim() === summary.merchantId &&
        !settings.clientId.trim() && !settings.clientSecret.trim() && !settings.postKey.trim() &&
        settings.appId === String(summary.appId || '');
      const payload = disablingExistingBinding ? { enabled: false } : {
        enabled: settings.enabled,
        environment: settings.environment,
        merchantId: settings.merchantId.trim(),
        ...(settings.clientId.trim() ? { clientId: settings.clientId.trim() } : {}),
        ...(settings.clientSecret.trim() ? { clientSecret: settings.clientSecret.trim() } : {}),
        ...(settings.postKey.trim() ? { postKey: settings.postKey.trim() } : {}),
        ...(settings.appId.trim() ? { appId: settings.appId.trim() } : {}),
      };
      const response = await apiFetch(`${apiUrl}/api/courier/integrations/bee`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Não foi possível salvar a integração Bee Delivery.');
      if (!mountedRef.current || saveVersion !== saveVersionRef.current || currentSessionRef.current !== sessionToken || !isStoredSessionTokenCurrent(sessionToken)) return;
      setSummary(data);
      setSettings(toSettings(data));
      setSaved(true);
    } catch (saveError) {
      if (saveError.name !== 'AbortError' && mountedRef.current && saveVersion === saveVersionRef.current && currentSessionRef.current === sessionToken) {
        setError(saveError.message || 'Não foi possível salvar a integração Bee Delivery.');
      }
    } finally {
      if (saveVersion === saveVersionRef.current) {
        saveControllerRef.current = null;
        if (mountedRef.current && currentSessionRef.current === sessionToken) setSaving(false);
      }
    }
  };

  return (
    <Card className="configuracoes-card configuracoes-section">
      <CardContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1} mb={2}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">Integração logística</Typography>
            <Typography variant="h6">Bee Delivery</Typography>
          </Box>
          <Chip
            label={loading ? 'Carregando' : summary?.configured ? 'Credenciais configuradas' : 'Configuração pendente'}
            color={summary?.configured ? 'success' : 'default'}
            variant="outlined"
          />
        </Stack>
        <Stack spacing={2}>
          <Alert severity="info">
            A configuração é própria desta loja. Salvar credenciais prepara o vínculo. A contratação depende das capacidades e do perfil aprovados para esta loja.
          </Alert>
          {summary?.readiness?.canHireBee !== true && (
            <Alert severity="info">
              {summary?.readiness?.blockedReasons?.includes('STORE_NOT_LINKED')
                ? 'Abra o Brutos Entregas para conferir o vínculo desta loja antes de configurar a Bee.'
                : 'Aguardando homologação. Credenciais, webhook, código de entrega e perfil operacional precisam ser validados antes da liberação.'}
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {saved && <Alert severity="success">Integração salva para esta empresa.</Alert>}
          <FormControlLabel
            control={<Switch checked={settings.enabled} onChange={(event) => update('enabled', event.target.checked)} disabled={loading || saving || (!settings.enabled && !((!identityDirty && (summary?.readiness?.canEnableBee===true || summary?.readiness?.canHireBee===true)) || (localPreview && settings.environment === 'integrationtest')))} />}
            label="Ativar cotações Bee"
          />
          <TextField
            select
            label="Ambiente Bee Delivery"
            value={settings.environment}
            onChange={(event) => { update('environment', event.target.value); update('enabled', false); }}
            disabled={loading || saving}
            helperText="A liberação produtiva exige o perfil aprovado para o vínculo atual. O ambiente de teste é exclusivo do sandbox local."
            fullWidth
          >
            <MenuItem value="production">Produção</MenuItem>
            <MenuItem value="integrationtest" disabled={!localPreview}>Teste Bee (somente local)</MenuItem>
          </TextField>
          <TextField
            label="Identificador da loja Bee Delivery"
            value={settings.merchantId}
            onChange={(event) => update('merchantId', event.target.value)}
            disabled={loading || saving}
            autoComplete="off"
            fullWidth
          />
          <TextField
            label="Client ID"
            value={settings.clientId}
            onChange={(event) => update('clientId', event.target.value)}
            placeholder={summary?.clientIdMasked || 'Digite para configurar ou substituir'}
            helperText={summary?.clientIdMasked ? 'Deixe vazio para conservar o valor salvo.' : 'Informe o Client ID fornecido à sua empresa.'}
            disabled={loading || saving}
            autoComplete="off"
            fullWidth
          />
          <TextField
            label="Client Secret"
            type="password"
            value={settings.clientSecret}
            onChange={(event) => update('clientSecret', event.target.value)}
            placeholder={summary?.hasClientSecret ? 'Configurado; digite para substituir' : 'Digite o segredo'}
            helperText="O segredo não é exibido novamente após salvar."
            disabled={loading || saving}
            autoComplete="new-password"
            fullWidth
          />
          <TextField
            label="Identificador da aplicação no webhook"
            value={settings.appId}
            onChange={(event) => update('appId',event.target.value)}
            helperText="Informe o X-App-Id confirmado pela Bee. Ele pode ser diferente do Client ID."
            disabled={loading || saving}
            autoComplete="off"
            fullWidth
          />
          <TextField
            label="Post Key do webhook"
            type="password"
            value={settings.postKey}
            onChange={(event) => update('postKey', event.target.value)}
            placeholder={summary?.hasPostKey ? 'Configurada; digite para substituir' : 'Digite a chave do webhook'}
            helperText="No sandbox, deixe vazia. A Bee fornecerá esta chave para o webhook de produção."
            disabled={loading || saving}
            autoComplete="new-password"
            fullWidth
          />
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={save}
              disabled={loading || saving}
              startIcon={saving ? <CircularProgress color="inherit" size={18} /> : <CloudDoneIcon />}
            >
              {saving ? 'Salvando...' : 'Salvar integração'}
            </Button>
          </Stack>
        </Stack>
        {canManageHomologation && <>
          <BeeDeliveryHomologationPanel apiUrl={apiUrl} sessionToken={sessionToken} onChanged={()=>setConfigRevision(value=>value+1)} />
          <CourierIntentsPanel apiUrl={apiUrl} sessionToken={sessionToken} />
        </>}
      </CardContent>
    </Card>
  );
};

export default BeeDeliverySettingsPanel;
